import { OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaObserver } from '@angular/flex-layout';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationService } from 'services/application/application.service';
import { AuthService } from 'services/authentication/auth.service';
import { DialogService, OptionBoxDialogOptions } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';
import { VERSION_NUMBER } from 'services/version/version.service';
// import { IssueAlertInfo } from 'services/application/application.service';
var LoginComponent = /** @class */ (function () {
    function LoginComponent(appService, authService, errorService, mediaChange, route, router, splashService, userService, dialogService) {
        var _this = this;
        this.appService = appService;
        this.authService = authService;
        this.errorService = errorService;
        this.mediaChange = mediaChange;
        this.route = route;
        this.router = router;
        this.splashService = splashService;
        this.userService = userService;
        this.dialogService = dialogService;
        this.darkTheme = false;
        this._subRelease = new Subject();
        var self = this;
        this._failures = 0; // No login failures to start.
        // this.IssueAlert = self.appService.IssueAlerts && self.appService.IssueAlerts[0];
        this.LoginInProgress = false;
        this.ShowImpersonationBox = false;
        this.ShowingParent = true;
        this.ShowingUpdate = false;
        self.ViewReady = false;
        self.darkTheme = true;
        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
                ? 'sm'
                : self.mediaChange.isActive('md')
                    ? 'md'
                    : self.mediaChange.isActive('lg')
                        ? 'lg'
                        : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';
        self.mediaChange.media$.pipe(takeUntil(self._subRelease)).subscribe(function (media) {
            self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
            self.LastSizeCode = media.mqAlias;
        });
        self.appService.ThemeSubject.pipe(takeUntil(self._subRelease)).subscribe(function (useDark) {
            self.darkTheme = useDark;
        });
        // Subscribe to the system online status subject. If it triggers as online, we can load the rest of the page items needed for the page.
        this.authService.SystemOnlineSubject.pipe(takeUntil(this._subRelease)).subscribe(function (result) {
            if (result && result.IRISVersion && result.IRISVersion !== VERSION_NUMBER) {
                // Different version number. Prompt the user to refresh, but only if we're not currently already
                // showing that dialog box. No need to stack dialog boxes.
                if (!self.ShowingUpdate) {
                    self.ShowingUpdate = true;
                    self.dialogService
                        .OpenMessageBoxDialog({
                        TitleText: 'New IRIS Version Available',
                        MessageText: "The application has determined that a new version of the IRIS System is " +
                            "available. New versions typically contain important bug fixes or feature upgrades. In order to " +
                            "log in to IRIS, you will need to refresh your browser and load the newest version of " +
                            "IRIS. Click the \"OK\" button to refresh the login screen and continue.",
                        OKText: 'OK',
                    })
                        .subscribe(function () {
                        location.reload(true);
                    });
                }
            }
        });
        // Set up a subscription on Test Connection, to check for the system being available while the user is sitting on the landing page.
        timer(0, 60000)
            .pipe(takeUntil(this._subRelease))
            .subscribe(function (_) {
            // Call out to test the connection.
            _this.authService.TestConnection().subscribe(function (result) {
                // Update the status.
                _this.authService.SetSystemOnlineStatus(result);
            }, function (err) {
                _this.authService.SetSystemOnlineStatus({
                    Status: false,
                    SystemMessage: '',
                    IRISVersion: VERSION_NUMBER,
                });
            });
        });
    }
    LoginComponent.prototype.ngOnDestroy = function () {
        this._subRelease.next();
        this._subRelease.complete();
    };
    LoginComponent.prototype.ngOnInit = function () { };
    LoginComponent.prototype.onSubmit = function (form) {
        var _this = this;
        var self = this;
        // Check to see if we have everything needed. We need a username and a password, and the password cannot have an @
        // symbol in it.
        if (!form.username) {
            self.splashService.SnackbarError('Please provide a username.', 5000);
            document.getElementById('username').focus();
            return;
        }
        if (!form.password) {
            self.splashService.SnackbarError('Please provide a password.', 5000);
            document.getElementById('password').focus();
            return;
        }
        // Check the username for an '@' symbol. If found, don't allow.
        if (form.username && form.username.indexOf('@') >= 0) {
            self.dialogService
                .OpenMessageBoxDialog({
                TitleText: 'Username Issue Detected',
                MessageText: "You appear to be attempting to use an email address as your username. You do not need to provide the \"@polk-fl.net\" " +
                    "portion of your email address for your username.",
                OKText: 'OK',
            })
                .subscribe();
            document.getElementById('username').focus();
            return;
        }
        if (self.LoginInProgress === true) {
            return;
        }
        self.LoginInProgress = true;
        self.splashService.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Logging in...' });
        // Grab the screen and window resolutions
        var scr = window.screen.width + 'x' + window.screen.height;
        var win = window.innerWidth + 'x' + window.innerHeight;
        self.authService.ValidateUser(form.username, form.password, form.impersonated, form.training, scr, win).subscribe(function (info) {
            // Drop the splash that's on the screen.
            self.splashService.CloseSplash();
            self.LoginInProgress = false;
            // Use the user service to process this login, which will handle routing to the proper home page for us.
            _this.authService.ProcessLogin(info);
        }, function (error) {
            _this.splashService.CloseSplash();
            _this.LoginInProgress = false;
            // Fetch the error packet
            var errObj = self.errorService.RetrieveServiceError(error);
            if (errObj.ErrorObject.status === 401) {
                // Unauthorized. Typically a failed username/password.
                self.splashService.SnackbarError(errObj.ErrorMessage, 8000);
                self._failures++;
                if (self._failures === 3) {
                    // Time to pop up a choice box because they are still having problems.
                    var options = {
                        TitleText: 'Problems Logging In?',
                        MessageText: "You appear to be having some difficulty logging in to the system. Do you require any assistance?",
                        Options: [
                            {
                                DisplayText: 'I forgot my password.',
                                Color: 'warn',
                                Response: 'password',
                            },
                            {
                                DisplayText: "No - I'll try again",
                                Color: 'primary',
                                Response: 'cancel',
                            },
                        ],
                    };
                    self.dialogService.OpenOptionBoxDialog(options).subscribe(function (response) {
                        // Reset the failures back to 0.
                        self._failures = 0;
                        switch (response) {
                            case 'username': // Username forgotten. Collect email address and send on.
                                // this.ForgotUsername();
                                break;
                            case 'password': // Password forgotten. Collect email address and send on.
                                self.dialogService.OpenMessageBoxDialog({
                                    TitleText: 'Getting Password Help',
                                    MessageText: "To change your password, you can click on the \"Need Password Help?\" link " +
                                        "on the login form to go to the Password Reset Tool. This tool can assist you in resetting " +
                                        "your password.",
                                    OKText: 'OK',
                                });
                                break;
                            case 'cancel': // User wishes to cancel and try again.
                                break;
                        }
                    });
                }
            }
            else {
                self.splashService.SnackbarError(errObj.ErrorMessage, 8000);
            }
        });
    };
    // #region Show Impersonation Method
    LoginComponent.prototype.ShowImpersonation = function (event) {
        if (!event) {
            return;
        }
        if (event.ctrlKey || event.metaKey || event.tapCount === 2) {
            this.ShowImpersonationBox = !this.ShowImpersonationBox;
        }
    };
    return LoginComponent;
}());
export { LoginComponent };
